/**
 * Parwaaz backend — the in-memory Store adapter.
 *
 * A plain, JSON-serialisable `Snapshot` object holds the whole register. Used directly by tests,
 * by the Node dev server (flushed to a JSON file after every mutation) and by the in-browser
 * demo (flushed to IndexedDB by whoever wires `onFlush`). Every write ends with `flush()`.
 */
import type { Application, AuditRow, ScanRow, Staff } from '@parwaaz/shared';
import { ApplicationFilter, BusyError, Card, ConflictError, NotFoundError, Store, StoredFile } from '../../store';

export interface BlocklistEntry {
  docNumber?: string;
  name?: string;
  dob?: string;
  reason: string;
}

export interface Snapshot {
  applications: Application[];
  audit: AuditRow[];
  config: Record<string, string>;
  staff: Staff[];
  staffSecrets: Record<string, string>;
  secrets: Record<string, string>;
  files: Record<string, StoredFile>;
  cards: Card[];
  scans: ScanRow[];
  blocklist: BlocklistEntry[];
}

export function emptySnapshot(): Snapshot {
  return {
    applications: [],
    audit: [],
    config: {},
    staff: [],
    staffSecrets: {},
    secrets: {},
    files: {},
    cards: [],
    scans: [],
    blocklist: [],
  };
}

const NEGATIVE_STATES = new Set(['Rejected', 'Revoked', 'Expired']);

export class MemoryStore implements Store {
  private snap: Snapshot;
  private auditSeq: number;
  /** Called at the end of every flush() — the dev server writes the file here, the demo writes IndexedDB. */
  onFlush?: (snap: Snapshot) => void;

  constructor(initial?: Snapshot) {
    this.snap = initial ?? emptySnapshot();
    this.auditSeq = this.snap.audit.reduce((max, r) => Math.max(max, r.seq), 0);
  }

  static fromJSON(json: string): MemoryStore {
    const store = new MemoryStore();
    store.fromJSON(json);
    return store;
  }

  toJSON(): string {
    return JSON.stringify(this.snap);
  }

  fromJSON(json: string): void {
    this.snap = JSON.parse(json) as Snapshot;
    this.auditSeq = this.snap.audit.reduce((max, r) => Math.max(max, r.seq), 0);
  }

  /** Direct access to the live snapshot — used by tests and by seed/demo.ts. */
  snapshot(): Snapshot {
    return this.snap;
  }

  flush(): void {
    this.onFlush?.(this.snap);
  }

  // ── applications ──────────────────────────────────────────────────────────

  findByIdem(idemKey: string): Application | null {
    return this.snap.applications.find((a) => a.idemKey === idemKey) ?? null;
  }

  getApplication(ref: string): Application | null {
    return this.snap.applications.find((a) => a.ref === ref) ?? null;
  }

  appendApplication(app: Application): void {
    this.snap.applications.push(app);
    this.flush();
  }

  updateApplication(ref: string, patch: Partial<Application>, expectedUpdatedAt?: string): Application {
    const idx = this.snap.applications.findIndex((a) => a.ref === ref);
    if (idx === -1) throw new NotFoundError(`no application '${ref}'`);
    const current = this.snap.applications[idx]!;
    if (expectedUpdatedAt !== undefined && current.updatedAt !== expectedUpdatedAt) {
      throw new ConflictError();
    }
    const updated: Application = { ...current, ...patch, ref: current.ref, updatedAt: new Date().toISOString() };
    this.snap.applications[idx] = updated;
    this.flush();
    return updated;
  }

  listApplications(filter?: ApplicationFilter): Application[] {
    let list = this.snap.applications;
    if (filter?.season) list = list.filter((a) => a.season === filter.season);
    if (filter?.states?.length) list = list.filter((a) => filter.states!.includes(a.status));
    if (filter?.search) {
      const q = filter.search.toLowerCase();
      list = list.filter(
        (a) =>
          a.ref.toLowerCase().includes(q) ||
          `${a.fields.givenNames} ${a.fields.surname}`.toLowerCase().includes(q) ||
          a.fields.docNumber.toLowerCase().includes(q) ||
          (a.card?.cardNo ?? '').toLowerCase().includes(q),
      );
    }
    const sorted = [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return filter?.limit ? sorted.slice(0, filter.limit) : sorted;
  }

  findActiveByDocNumber(docNumber: string, season: string): Application | null {
    return (
      this.snap.applications.find(
        (a) => a.season === season && a.fields.docNumber === docNumber && !NEGATIVE_STATES.has(a.status),
      ) ?? null
    );
  }

  // ── audit ─────────────────────────────────────────────────────────────────

  appendAudit(row: Omit<AuditRow, 'seq'>): AuditRow {
    this.auditSeq += 1;
    const full: AuditRow = { ...row, seq: this.auditSeq };
    this.snap.audit.push(full);
    this.flush();
    return full;
  }

  listAudit(entityId?: string): AuditRow[] {
    const rows = entityId ? this.snap.audit.filter((r) => r.entityId === entityId) : this.snap.audit;
    return [...rows].sort((a, b) => a.seq - b.seq);
  }

  // ── config ────────────────────────────────────────────────────────────────

  getConfig(key: string): string | null {
    return this.snap.config[key] ?? null;
  }

  setConfig(key: string, value: string): void {
    this.snap.config[key] = value;
    this.flush();
  }

  allConfig(): Record<string, string> {
    return { ...this.snap.config };
  }

  // ── staff ─────────────────────────────────────────────────────────────────

  listStaff(): Staff[] {
    return [...this.snap.staff];
  }

  getStaff(user: string): Staff | null {
    return this.snap.staff.find((s) => s.user === user) ?? null;
  }

  upsertStaff(staff: Staff): void {
    const idx = this.snap.staff.findIndex((s) => s.user === staff.user);
    if (idx === -1) this.snap.staff.push(staff);
    else this.snap.staff[idx] = staff;
    this.flush();
  }

  getStaffSecret(user: string): string | null {
    return this.snap.staffSecrets[user] ?? null;
  }

  setStaffSecret(user: string, hash: string): void {
    this.snap.staffSecrets[user] = hash;
    this.flush();
  }

  // ── secrets ───────────────────────────────────────────────────────────────

  getSecret(name: string): string | null {
    return this.snap.secrets[name] ?? null;
  }

  setSecret(name: string, value: string): void {
    this.snap.secrets[name] = value;
    this.flush();
  }

  deleteSecret(name: string): void {
    delete this.snap.secrets[name];
    this.flush();
  }

  // ── files ─────────────────────────────────────────────────────────────────

  putFile(ref: string, slot: string, name: string, mime: string, dataB64: string): string {
    const id = `${ref}-${slot}-${globalThis.crypto.randomUUID().slice(0, 8)}`;
    const size = Math.floor((dataB64.length * 3) / 4);
    this.snap.files[id] = { id, name, mime, data: dataB64, size };
    this.flush();
    return id;
  }

  getFile(fileId: string): StoredFile | null {
    return this.snap.files[fileId] ?? null;
  }

  deleteFile(fileId: string): void {
    delete this.snap.files[fileId];
    this.flush();
  }

  // ── cards ─────────────────────────────────────────────────────────────────

  allocateCardNo(prefix: string, seasonYear: string): string {
    const seq = Number(this.snap.config.card_seq ?? '0') + 1;
    if (!Number.isFinite(seq) || seq > 999_999) throw new BusyError();
    this.snap.config.card_seq = String(seq);
    this.flush();
    return `${prefix}-${seasonYear}-${String(seq).padStart(6, '0')}`;
  }

  listCards(): Card[] {
    return [...this.snap.cards];
  }

  upsertCard(card: Card): void {
    const idx = this.snap.cards.findIndex((c) => c.cardNo === card.cardNo);
    if (idx === -1) this.snap.cards.push(card);
    else this.snap.cards[idx] = card;
    this.flush();
  }

  // ── scans ─────────────────────────────────────────────────────────────────

  appendScans(rows: ScanRow[]): void {
    this.snap.scans.push(...rows);
    this.flush();
  }

  listScans(sinceIso?: string): ScanRow[] {
    return sinceIso ? this.snap.scans.filter((s) => s.at >= sinceIso) : [...this.snap.scans];
  }

  // ── blocklist ─────────────────────────────────────────────────────────────

  listBlocklist(): BlocklistEntry[] {
    return [...this.snap.blocklist];
  }
}
